import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChevronDown, ChevronUp, Edit, Save, FileText, ChevronRight, ChevronLeft } from 'lucide-react';

const EDITABLE_FIELDS = [
  { key: 'customer_name', label: 'Customer Name', type: 'text' },
  { key: 'nmi', label: 'NMI', type: 'text', mono: true },
  { key: 'site_address', label: 'Site Address', type: 'text' },
  { key: 'retailer_name', label: 'Retailer', type: 'text' },
  { key: 'total_kwh', label: 'Total kWh', type: 'number', step: '0.01' },
  { key: 'peak_kwh', label: 'Peak kWh', type: 'number', step: '0.01' },
  { key: 'shoulder_kwh', label: 'Shoulder kWh', type: 'number', step: '0.01' },
  { key: 'off_peak_kwh', label: 'Off-Peak kWh', type: 'number', step: '0.01' },
  { key: 'supply_days', label: 'Supply Days', type: 'number' },
  { key: 'daily_supply_charge', label: 'Daily Supply Charge ($/day)', type: 'number', step: '0.001' },
  { key: 'usage_rate_flat', label: 'Flat Usage Rate ($/kWh)', type: 'number', step: '0.0001' },
  { key: 'usage_rate_peak', label: 'Peak Rate ($/kWh)', type: 'number', step: '0.0001' },
  { key: 'usage_rate_shoulder', label: 'Shoulder Rate ($/kWh)', type: 'number', step: '0.0001' },
  { key: 'usage_rate_off_peak', label: 'Off-Peak Rate ($/kWh)', type: 'number', step: '0.0001' },
  { key: 'total_bill_amount', label: 'Total Bill Amount ($)', type: 'number', step: '0.01' },
  { key: 'discount_amount', label: 'Discount Amount ($)', type: 'number', step: '0.01' },
  { key: 'solar_feed_in_kwh', label: 'Solar Export kWh', type: 'number', step: '0.01' },
  { key: 'solar_feed_in_rate', label: 'Solar Feed-in Rate ($/kWh)', type: 'number', step: '0.0001' },
];

function BillCard({ bill, editedData, onFieldChange }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const ed = editedData || {};

  return (
    <Card className="shadow-sm border-slate-200" data-testid={'bulk-review-bill-' + bill.bill_id}>
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <FileText className="w-5 h-5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold text-primary">NMI: {ed.nmi || 'N/A'}</span>
            <span className="font-medium text-slate-800">{ed.customer_name || 'Unknown'}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            <span>{ed.total_kwh ? ed.total_kwh.toFixed(1) + ' kWh' : '—'}</span>
            <span>{ed.supply_days ? ed.supply_days + ' days' : '—'}</span>
            <span>{ed.total_bill_amount ? '$' + ed.total_bill_amount.toFixed(2) : '—'}</span>
            <span className="text-slate-400">({bill.filename})</span>
          </div>
        </div>
        <Badge variant="outline" className="shrink-0 text-xs">
          {expanded ? 'Collapse' : 'Review'}
        </Badge>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </div>

      {expanded && (
        <CardContent className="border-t pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Review and edit extracted data</p>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setEditing(!editing); }}
              data-testid={'bulk-edit-toggle-' + bill.bill_id}
            >
              {editing ? <Save className="w-3 h-3 mr-1" /> : <Edit className="w-3 h-3 mr-1" />}
              {editing ? 'Done' : 'Edit'}
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {EDITABLE_FIELDS.map((field) => {
              const val = ed[field.key];
              if (val === null && !editing) return null;
              return (
                <div key={field.key}>
                  <Label className="text-xs text-slate-500">{field.label}</Label>
                  <Input
                    type={field.type}
                    step={field.step}
                    value={val ?? ''}
                    onChange={(e) => {
                      const v = field.type === 'number'
                        ? (e.target.value === '' ? null : parseFloat(e.target.value))
                        : e.target.value;
                      onFieldChange(bill.bill_id, field.key, v);
                    }}
                    disabled={!editing}
                    className={'bg-white h-8 text-sm ' + (field.mono ? 'font-mono' : '')}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export function BulkReviewStep({ bulkResults, bulkEditedData, onBulkFieldChange, onBack, onContinue }) {
  const successfulBills = (bulkResults?.results || []).filter((r) => r.success);

  return (
    <div className="space-y-4 mt-4" data-testid="bulk-review-step">
      <Card className="border-primary/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Review Extracted Data</CardTitle>
          <p className="text-sm text-slate-500">
            {successfulBills.length} bill{successfulBills.length !== 1 ? 's' : ''} processed.
            Click each bill to review and edit the extracted data before selecting rates.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {successfulBills.map((bill) => (
            <BillCard
              key={bill.bill_id}
              bill={bill}
              editedData={bulkEditedData[bill.bill_id] || bill.extracted_data || {}}
              onFieldChange={onBulkFieldChange}
            />
          ))}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onBack} className="flex-1">
              <ChevronLeft className="w-4 h-4 mr-1" />Back
            </Button>
            <Button onClick={onContinue} className="flex-1 bg-primary hover:bg-primary/90" data-testid="bulk-review-continue-btn">
              Continue to Rate Selection
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
